import { useQuery } from "@tanstack/react-query";
import { createRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useDocumentTitle } from "usehooks-ts";
import { trpc } from "@/components/QueryProvider";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { authClient, isAdminRole } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { loginRoute } from "./loginRoute";
import { rootRoute } from "./rootRoute";

// Formats a date as dd/mm/yyyy.
function formatDate(date: Date): string {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
}

// Highlights paying organisations green and expired-trial organisations red.
// A subscription takes precedence: a paying org is never shown as expired.
function rowClassName(subscriptionId: string | null, trialEndsAt: Date | null): string | undefined {
    if (subscriptionId) return "bg-green-100 hover:bg-green-200";
    if (trialEndsAt && trialEndsAt.getTime() < Date.now()) return "bg-red-100 hover:bg-red-200";
    return undefined;
}

function AdminUsersPage() {
    useDocumentTitle("All users - Admin - Orsane");
    const { data: users, isLoading } = useQuery(trpc.admin.listAllUsers.queryOptions());
    const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

    const handleImpersonate = async (userId: string, email: string) => {
        if (!window.confirm(`Impersonate ${email}? You will be signed in as this user.`)) {
            return;
        }
        setImpersonatingId(userId);
        const { error } = await authClient.admin.impersonateUser({ userId });
        if (error) {
            toast.error(error.message ?? "Failed to impersonate user");
            setImpersonatingId(null);
            return;
        }
        // Land in the impersonated user's app with a fresh session.
        window.location.assign("/");
    };

    return (
        <div className="min-h-screen bg-orsane-background p-8">
            <div className="w-full">
                <h1 className="mb-1 text-xl font-semibold">All users</h1>
                <p className="mb-6 text-sm text-muted-foreground">
                    Every user across all organisations. Super admin only.
                </p>

                {isLoading ? (
                    <div className="text-sm text-muted-foreground">Loading…</div>
                ) : (
                    <div className="rounded-md border bg-white">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Email</TableHead>
                                    <TableHead>User ID</TableHead>
                                    <TableHead>Created at</TableHead>
                                    <TableHead>Organisation ID</TableHead>
                                    <TableHead>Organisation name</TableHead>
                                    <TableHead>Trial ends</TableHead>
                                    <TableHead>Subscription ID</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users?.map((u, i) => (
                                    <TableRow
                                        key={`${u.userId}-${u.organizationId ?? "none"}-${i}`}
                                        className={cn(rowClassName(u.subscriptionId, u.trialEndsAt))}
                                    >
                                        <TableCell>{u.email}</TableCell>
                                        <TableCell className="font-mono text-xs">{u.userId}</TableCell>
                                        <TableCell className="whitespace-nowrap">{formatDate(u.createdAt)}</TableCell>
                                        <TableCell className="font-mono text-xs">{u.organizationId ?? "—"}</TableCell>
                                        <TableCell>{u.organizationName ?? "—"}</TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            {u.trialEndsAt ? formatDate(u.trialEndsAt) : "—"}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">{u.subscriptionId ?? "—"}</TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                disabled={impersonatingId !== null}
                                                onClick={() => handleImpersonate(u.userId, u.email)}
                                            >
                                                {impersonatingId === u.userId ? "Impersonating…" : "Impersonate"}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
}

export const adminUsersRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/admin/users",
    beforeLoad: async () => {
        const session = await authClient.getSession();
        if (!session.data?.user) {
            throw redirect({
                to: loginRoute.to,
                search: {
                    redirect: window.location.href,
                },
            });
        }
        if (!isAdminRole(session.data.user.role)) {
            // Not a super admin — send them back to the app.
            throw redirect({ to: "/" });
        }
    },
    component: AdminUsersPage,
});
